import { Flex } from '@mantine/core';
import classNames from 'classnames';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';

import { AsyncDisplay, showError, Skeleton, Text } from '@feathr/components';
import GrowthCreditIcon from '@feathr/extender/components/GrowthCreditIcon/GrowthCreditIcon';
import { useAccount, useLocalUrl } from '@feathr/extender/state';

import * as styles from './CreditsCard.css';

function CreditsCard(): JSX.Element {
  const { t } = useTranslation();
  const localUrl = useLocalUrl();
  const account = useAccount();
  const match = useRouteMatch<{ accountId: string; primary: string; secondary?: string }>({
    path: '/:accountId/:primary/:secondary?',
  });

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [credits, setCredits] = useState<number>(0);

  useEffect(() => {
    async function loadGrowthCredits(): Promise<void> {
      setIsLoading(true);
      try {
        const stats = await account.getGrowthCredits();
        setCredits(stats.credit_balance);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    }

    loadGrowthCredits();
  }, [account, t]);

  const isActive =
    match?.params.primary === 'fundraise' && match?.params.secondary === 'growth-credits';

  const formattedCredits = credits.toLocaleString();

  return (
    <Link
      className={classNames(styles.link, { [styles.active]: isActive })}
      to={localUrl('/fundraise/growth-credits')}
    >
      <div className={classNames(styles.root, { [styles.active]: isActive })}>
        <AsyncDisplay isLoading={isLoading} skeleton={<Skeleton paragraphs={1} width={'100%'} />}>
          <Flex align={'flex-start'} gap={'sm'}>
            <GrowthCreditIcon />
            <Flex direction={'column'}>
              <Text.DefaultSemibold>{formattedCredits}</Text.DefaultSemibold>
              <Text.SmallRegular className={styles.label}>{t('credits')}</Text.SmallRegular>
            </Flex>
          </Flex>
        </AsyncDisplay>
      </div>
    </Link>
  );
}

export default CreditsCard;
